/**
 * `src/platform/auth/` — raw JWT decode + the unguarded predicates. **Sole copy.**
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ## ⛔⛔ `isTokenExpired` / `isTokenExpiringSoon` IN THIS FILE FAIL OPEN ON AN ABSENT `exp`
 *
 * Measured at `:68` and `:75` below, and executed with a positive control:
 *
 * ```
 *   return Date.now() >= (decoded.exp - withinSeconds) * 1000;
 *
 *   POSITIVE CONTROL (exp in the past):  isTokenExpired = true    isTokenExpiringSoon = true
 *   THE DEFECT       (exp ABSENT):       isTokenExpired = FALSE   isTokenExpiringSoon = FALSE
 *                                        (undefined - 300) -> NaN ; Date.now() >= NaN -> false
 * ```
 *
 * ⇒ **A token with no `exp` claim reads as "not expired" and "not expiring" — forever.** That is
 * the live session-liveness path: `useAuth`'s proactive-refresh timer calls `isTokenExpiringSoon`,
 * so the session would silently never refresh and every later call would 401 while the UI still
 * said signed-in. `decodeToken` also ends in a bare `payload as DecodedToken`: an unchecked cast of
 * attacker-influenced JSON onto a type declaring `exp: number` as REQUIRED, so TypeScript cannot
 * warn at any call site. Filed as **#1188**.
 *
 * ⛔ **`platform/auth/jwt.ts` is the guarded seam every consumer must use.** It re-exports
 * `decodeToken`/`DecodedToken` from here and deliberately does NOT re-export the two predicates
 * below — it defines fail-CLOSED replacements over `hasUsableExpiry`. Import the predicates from
 * `./jwt`, never from this file.
 *
 * ⛔ Not exploitable today: `cp-auth-api` always signs an `expiresIn`. It is guarded anyway,
 * because the shell should not depend on an upstream invariant nobody asserts.
 * `./jwt.ts` carries the record of how this was nearly missed — read it before touching either
 * predicate.
 */

/** Decoded JWT payload shape (Plan 23 PR0a).
 *
 * The legacy flat `role` enum was removed by Plan 23 (Hard rule 1 — no
 * backward compatibility). Auth gating now uses `siteRoles` (slugs from
 * `cp_auth.role_definitions`) + `sitePermissions` (the union of every
 * role's `permissions[]`, expanded by the snapshot-builder at JWT-issue
 * time). Resource-scoped grants (app_owner, dp_operator, ...) are NOT
 * expanded here — they go through `@RequireResourcePermission` server-side.
 *
 * The arrays are typed as required because every legitimate CP access
 * token carries them. A missing claim means the token is from a stale
 * client or a buggy minter; either should fail closed in consumers
 * (treat as empty).
 */
export interface DecodedToken {
  sub: string;
  email: string;
  siteRoles: readonly string[];
  sitePermissions: readonly string[];
  scope: string;
  iat: number;
  exp: number;
}

/**
 * Decode a base64url-encoded JWT segment to its UTF-8 string body.
 *
 * JWTs use base64url (RFC 7519 §3.1 / RFC 4648 §5) — `-` and `_` instead of
 * `+` and `/`, with padding `=` stripped. `atob()` only accepts standard
 * base64, so feeding it a raw segment fails for any payload byte that maps
 * to those substitution chars (or any segment whose byte length isn't a
 * multiple of 3, which strips padding). Without this normalization, valid
 * tokens minted by `jsonwebtoken` (the lib `cp-auth-api` uses) decode-fail
 * silently and `decodeToken` returns `null` — reproducing the empty-sidebar
 * symptom the JWT-hydration fix was meant to close.
 *
 * Decoding the bytes as UTF-8 (not Latin-1, which is what `atob` returns)
 * matters too: an `email` claim with non-ASCII chars would otherwise come
 * back mojibake'd.
 */
function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  const standard = padded + '='.repeat(padLen);
  const binary = atob(standard);
  // atob returns each byte as a single Latin-1 code unit; map back to bytes
  // and decode as UTF-8 to preserve multi-byte chars (e.g. accented emails).
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Decode a JWT payload without verifying the signature (client-side only). */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return payload as DecodedToken;
  } catch {
    return null;
  }
}

/** ⛔ FAILS OPEN on an absent `exp` — see the file header. Use `./jwt`'s guarded version. */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded) return true;
  return Date.now() >= decoded.exp * 1000;
}

/** ⛔ FAILS OPEN on an absent `exp` — see the file header. Use `./jwt`'s guarded version. */
export function isTokenExpiringSoon(token: string, withinSeconds: number): boolean {
  const decoded = decodeToken(token);
  if (!decoded) return true;
  return Date.now() >= (decoded.exp - withinSeconds) * 1000;
}

/*
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ## ⛔ WHAT WAS DELIBERATELY NOT COPIED, AND WHY IT MUST NOT COME BACK
 *
 * The module this was lifted from also exported `hasValidScope` and a five-function
 * password-strength family (`validatePasswordStrength`, `passwordStrengthLevel`,
 * `passwordStrengthColor`, `strengthLabel`, `passwordStrengthPercent`). None of them was
 * copied. Measured: **zero call sites** for any of the six anywhere outside their own
 * definitions and tests.
 *
 * ⛔ Two independent reasons, either sufficient:
 *
 *   1. **This console deliberately has no client-side password policy.** `pages/
 *      ChangePasswordPage.tsx` says so in its own words — the confirm-match check is
 *      client-side only and *"the server owns every strength rule."* Copying
 *      `validatePasswordStrength` would plant a second 12-char/upper/lower/digit rule beside
 *      the real one in `cp-auth-api/src/modules/auth/auth.service.ts`, sharing its NAME, with
 *      nothing keeping the two in step. A divergent duplicate of a security rule is worse than
 *      no duplicate, and the shared name is what would make a future grep read as "wired up".
 *   2. **`passwordStrengthColor` emits literal state-colour classes** (`bg-state-critical`,
 *      `bg-state-warning`, `bg-state-success`). Copying it took `data-tone.test.tsx`'s ratchet
 *      from 132 to 135 against a ceiling of 134 — measured, and the ratchet was right: those
 *      are colour decisions taken at a call site, on code nothing calls.
 *
 * ⇒ If a strength indicator is ever wanted here, it is a new component routed through
 * `shell/data-tone.tsx`, not a restored copy of this one.
 */
